import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class Licence extends BaseObj {
    public ID: number;
    public __id: number;

    public AgeFrom: number;
    public __agefrom: number;

    public AgeTo: number;
    public __ageto: number;

    public readonly FederationID: number;

    public IsChild: boolean;
    public __ischild: boolean;

    public IsExcercise: boolean;
    public __isexcercise: boolean;

    public IsForeignCitizen: boolean;
    public __isforeigncitizen: boolean;

    public Name: string;
    public __name: string;

    public OneTimeLicence: boolean;
    public __onetimelicence: boolean;

    public Price: number | null;
    public __price: number | null;

    public RequireClub: boolean;
    public __requireclub: boolean;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        AgeFrom?: number;
        AgeTo?: number;
        EntityState?: EntityState;
        FederationID?: number;
        IsChild?: boolean;
        IsExcercise?: boolean;
        IsForeignCitizen?: boolean;
        Name?: string | null;
        OneTimeLicence?: boolean;
        Price?: number | null;
        RequireClub?: boolean;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<Licence>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('Licence tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.AgeFrom = values.AgeFrom ?? 0;
        this.__agefrom = values.AgeFrom ?? 0;
        Object.defineProperty(this, 'AgeFrom', {
            get(): number {
                return this.__agefrom;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('AgeFrom', this.__agefrom, value);
                this.__agefrom = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.AgeTo = values.AgeTo ?? 0;
        this.__ageto = values.AgeTo ?? 0;
        Object.defineProperty(this, 'AgeTo', {
            get(): number {
                return this.__ageto;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('AgeTo', this.__ageto, value);
                this.__ageto = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.FederationID = values.FederationID ?? 0;

        this.IsChild = values.IsChild ?? false;
        this.__ischild = values.IsChild ?? false;
        Object.defineProperty(this, 'IsChild', {
            get(): boolean {
                return this.__ischild;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('IsChild', this.__ischild, value);
                this.__ischild = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.IsExcercise = values.IsExcercise ?? false;
        this.__isexcercise = values.IsExcercise ?? false;
        Object.defineProperty(this, 'IsExcercise', {
            get(): boolean {
                return this.__isexcercise;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('IsExcercise', this.__isexcercise, value);
                this.__isexcercise = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.IsForeignCitizen = values.IsForeignCitizen ?? false;
        this.__isforeigncitizen = values.IsForeignCitizen ?? false;
        Object.defineProperty(this, 'IsForeignCitizen', {
            get(): boolean {
                return this.__isforeigncitizen;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('IsForeignCitizen', this.__isforeigncitizen, value);
                this.__isforeigncitizen = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Name = values.Name ?? '';
        this.__name = values.Name ?? '';
        Object.defineProperty(this, 'Name', {
            get(): string {
                return this.__name;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Name', this.__name, value);
                this.__name = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.OneTimeLicence = values.OneTimeLicence ?? false;
        this.__onetimelicence = values.OneTimeLicence ?? false;
        Object.defineProperty(this, 'OneTimeLicence', {
            get(): boolean {
                return this.__onetimelicence;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('OneTimeLicence', this.__onetimelicence, value);
                this.__onetimelicence = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Price = values.Price ?? null;
        this.__price = values.Price ?? null;
        Object.defineProperty(this, 'Price', {
            get(): number | null {
                return this.__price;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('Price', this.__price, value);
                this.__price = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RequireClub = values.RequireClub ?? false;
        this.__requireclub = values.RequireClub ?? false;
        Object.defineProperty(this, 'RequireClub', {
            get(): boolean {
                return this.__requireclub;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('RequireClub', this.__requireclub, value);
                this.__requireclub = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"AgeFrom": ${JSON.stringify(this.AgeFrom)}` +
            ',' +
            `"AgeTo": ${JSON.stringify(this.AgeTo)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"FederationID": ${JSON.stringify(this.FederationID)}` +
            ',' +
            `"IsChild": ${JSON.stringify(this.IsChild)}` +
            ',' +
            `"IsExcercise": ${JSON.stringify(this.IsExcercise)}` +
            ',' +
            `"IsForeignCitizen": ${JSON.stringify(this.IsForeignCitizen)}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"OneTimeLicence": ${JSON.stringify(this.OneTimeLicence)}` +
            ',' +
            `"Price": ${JSON.stringify(this.Price)}` +
            ',' +
            `"RequireClub": ${JSON.stringify(this.RequireClub)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of Licence from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        AgeFrom?: number;
        AgeTo?: number;
        IsChild?: boolean;
        IsExcercise?: boolean;
        IsForeignCitizen?: boolean;
        Name?: string | null;
        OneTimeLicence?: boolean;
        Price?: number | null;
        RequireClub?: boolean;
        EntityState?: 'Added' | 'Unchanged';
    }): Licence {
        return new Licence(values);
    }

    public static _FromServerJson(json: any): Licence {
        return new Licence({
            ID: json.ID,
            AgeFrom: json.AgeFrom,
            AgeTo: json.AgeTo,
            FederationID: json.FederationID,
            IsChild: json.IsChild,
            IsExcercise: json.IsExcercise,
            IsForeignCitizen: json.IsForeignCitizen,
            Name: json.Name ?? '',
            OneTimeLicence: json.OneTimeLicence,
            Price: json.Price ?? null,
            RequireClub: json.RequireClub,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of Licence from another instance of Licence.
     * @param other The Licence you want to clone
     * @returns A cloned Licence
     */
    public static FromOther(other: Licence): Licence {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return Licence._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return Licence.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'AgeFrom'
            | 'AgeTo'
            | 'FederationID'
            | 'IsChild'
            | 'IsExcercise'
            | 'IsForeignCitizen'
            | 'Name'
            | 'OneTimeLicence'
            | 'Price'
            | 'RequireClub'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'Licence';
}
